import { useEffect, useRef, useState } from "react";
import { UserPlus, Upload, Download, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useCollection } from "@/hooks/useCollection";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel, StatusBadge } from "./common";
import { DataTable } from "./DataTable";
import { FormDialog } from "./FormDialog";

function EmailInvites({ invites, onDone }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const openDlg = () => { setPicked(invites.filter((x) => !x.emailed_at).map((x) => x.user_id)); setOpen(true); };
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const send = async () => {
    if (!window.confirm(`Email login details to ${picked.length} team member(s)?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/team/login-invites/email", { user_ids: picked, app_url: window.location.origin });
      if (data.sent.length) toast.success(`Login emails sent to ${data.sent.length} people`);
      if (data.failed.length) toast.error(`${data.failed.length} failed: ${data.failed[0].error}`);
      setOpen(false); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <>
      <Button data-testid="invites-email-open" size="sm" variant="outline" onClick={openDlg}><Mail className="h-3.5 w-3.5 mr-1" />Email logins to staff ({invites.filter((x) => !x.emailed_at).length} not sent)</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="invites-email-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>Email first-login details</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500">Each person gets the CRM link, their login email and one-time password. They must set a new password on first sign-in.</p>
          <div className="flex gap-2 text-xs">
            <button data-testid="invites-select-all" className="text-[#A9792C] underline" onClick={() => setPicked(invites.map((x) => x.user_id))}>Select all</button>
            <button data-testid="invites-select-none" className="text-zinc-500 underline" onClick={() => setPicked([])}>Clear</button>
          </div>
          <div className="thin-scroll max-h-80 space-y-0.5 overflow-y-auto">
            {invites.map((x) => (
              <label key={x.user_id} data-testid={`invite-row-${x.user_id}`} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-zinc-50">
                <input type="checkbox" checked={picked.includes(x.user_id)} onChange={() => toggle(x.user_id)} className="h-4 w-4 accent-[#A9792C]" />
                <span className="min-w-0 flex-1"><span className="block truncate font-medium">{x.name}</span><span className="block truncate text-xs text-zinc-500">{x.email}</span></span>
                <span className={`shrink-0 text-[11px] ${x.email_error ? "text-rose-600" : x.emailed_at ? "text-emerald-600" : "text-zinc-400"}`}>{x.email_error ? "Failed" : x.emailed_at ? `Sent ${new Date(x.emailed_at).toLocaleDateString("en-IN")}` : "Not sent"}</span>
              </label>
            ))}
          </div>
          <Button data-testid="invites-email-send" disabled={busy || !picked.length} onClick={send}>{busy ? "Sending…" : `Send to ${picked.length}`}</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RosterTools({ onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [invites, setInvites] = useState([]);
  const loadInv = () => api.get("/team/login-invites").then((r) => setInvites(r.data)).catch(() => {});
  useEffect(() => { loadInv(); }, []);
  const pick = async (e) => {
    const f = e.target.files[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/team/import-roster", fd);
      toast.success(`Imported ${data.people} people · ${data.new_logins} new logins`);
      await loadInv(); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };
  const csv = async () => {
    const { data } = await api.get("/team/login-invites.csv", { responseType: "blob" });
    const u = URL.createObjectURL(data); const a = document.createElement("a"); a.href = u; a.download = "zds-first-logins.csv"; a.click(); URL.revokeObjectURL(u);
  };
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600">
      <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={pick} data-testid="roster-file-input" />
      <Button data-testid="roster-import-button" size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()}><Upload className="h-3.5 w-3.5 mr-1" />{busy ? "Importing…" : "Import team roster (.xlsx)"}</Button>
      {invites.length > 0 && <Button data-testid="roster-invites-download" size="sm" variant="outline" onClick={csv}><Download className="h-3.5 w-3.5 mr-1" />First-login passwords ({invites.length})</Button>}
      {invites.length > 0 && <EmailInvites invites={invites} onDone={loadInv} />}
      <span>Columns: name, joining date, designation, reports to, phone, email. New people get a login with a one-time password that must be changed on first sign-in.</span>
    </div>
  );
}

export function UsersPanel({ roles }) {
  const { items, reload } = useCollection("users");
  const [form, setForm] = useState(null);
  const roleOpts = roles.map((r) => ({ value: r.id, label: r.name }));
  const roleName = (u) => (u.role === "admin" ? "Administrator (all access)" : roles.find((r) => r.id === u.role_id)?.name || "—");

  const call = async (fn, msg) => {
    try { await fn(); toast.success(msg); await reload(); return true; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
  };
  const save = (f) => {
    const body = { name: f.name, email: f.email, password: f.password || null, role_id: f.role === "admin" ? null : f.role_id };
    return f.id ? call(() => api.patch(`/users/${f.id}`, body), "Login updated") : call(() => api.post("/users", body), "Login created");
  };
  const toggle = (u) => call(() => api.patch(`/users/${u.id}`, { name: u.name, email: u.email, disabled: !u.disabled }), u.disabled ? "Login enabled" : "Login disabled");

  const fields = [
    { name: "name", label: "Full name", required: true },
    { name: "email", label: "Email (login ID)", type: "email", required: true },
    ...(form?.role === "admin" ? [] : [{ name: "role_id", label: "Role", type: "select", options: roleOpts, required: true }]),
    { name: "password", label: form?.id ? "New password (leave blank to keep)" : "Initial password (min 6)", type: "password", required: !form?.id },
  ];

  return (
    <Panel testid="users-panel" title="Team logins" action={<Button data-testid="add-user-button" size="sm" onClick={() => setForm({})}><UserPlus className="h-3.5 w-3.5 mr-1" />Add login</Button>}>
      <RosterTools onDone={reload} />
      <DataTable testid="users-table" rows={items} columns={[
        { key: "name", label: "Name", render: (u) => <div><div className="font-semibold text-slate-800">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></div> },
        { key: "role", label: "Role", render: roleName },
        { key: "status", label: "Status", render: (u) => <StatusBadge testid={`user-status-${u.id}`} value={u.disabled ? "Disabled" : "Active"} /> },
        { key: "act", label: "", render: (u) => (
          <div className="flex gap-1">
            <Button data-testid={`user-edit-${u.id}`} size="sm" variant="ghost" onClick={() => setForm(u)}>Edit</Button>
            {u.role !== "admin" && <Button data-testid={`user-toggle-${u.id}`} size="sm" variant="ghost" className={u.disabled ? "" : "text-rose-600"} onClick={() => toggle(u)}>{u.disabled ? "Enable" : "Disable"}</Button>}
          </div>
        ) },
      ]} />
      <p className="mt-3 text-xs text-slate-500">Each login gets exactly the permissions of its role in the matrix above.</p>
      <FormDialog testid="user-form" open={!!form} onOpenChange={(o) => !o && setForm(null)} title={form?.id ? "Edit login" : "Add team login"} fields={fields} initial={form} onSubmit={save} />
    </Panel>
  );
}
